import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Paper, Box, Typography, Fab, useMediaQuery, Theme } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';
import OrderTimelineFilters from 'components/order-details/order-timeline/order-timeline-filters';
import OrderEventsTimeline from 'components/shared/order-events-timeline/order-events-timeline.component';
import { Order } from 'models/order';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { EventOrgTypeFilter, OrderEventFilters } from 'models/order-event';
import { formatPatientName } from 'utils/format';

type OrderTimelineProps = {
    order: Order;
};
const OrderTimeline: FC<OrderTimelineProps> = ({ order }: OrderTimelineProps) => {
    const { t: translate } = useTranslation(['orders']);
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
    const [orderEventFilters, setOrderEventFilters] = useState<OrderEventFilters>({
        chat: true,
        files: true,
        modified: true,
        status: true,
        comments: true,
    });
    const [orgTypeFilters, setOrgTypeFilters] = useState<EventOrgTypeFilter>({
        showClinicEvents: true,
        showLabEvents: true,
    });

    const handleFilterChange = (eventFilters: OrderEventFilters, orgFilters: EventOrgTypeFilter) => {
        setOrderEventFilters(eventFilters);
        setOrgTypeFilters(orgFilters);
    };

    const onScrollBottomClick = () => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth',
        });
    };

    return (
        <Fragment>
            <Grid container spacing={2}>
                <Grid item sm={12} lg={3}>
                    <Paper variant="outlined" square sx={{ position: 'sticky', top: 80 }}>
                        <Box p={2}>
                            <Box mb={1}>
                                <PageTitle headingStyle="h2" title={`${translate('orders:ORDER')}: ${order.orderId}`} />
                                <Typography variant="subtitle1" className="mouseflow-mask">
                                    {formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE'))}
                                </Typography>
                                {order.patient?.personalIdentityNumber ? (
                                    <Typography variant="subtitle1" className="mouseflow-mask">
                                        {order.patient?.personalIdentityNumber}
                                    </Typography>
                                ) : order.patient ? (
                                    <Typography variant="subtitle1">{translate('orders:NO_PIN_NUMBER')}</Typography>
                                ) : undefined}{' '}
                            </Box>
                            {isLargeScreen ? <OrderTimelineFilters onFilterChange={handleFilterChange} /> : undefined}
                        </Box>
                    </Paper>
                </Grid>
                <Grid item sm={12} lg={9}>
                    <OrderEventsTimeline
                        orderIds={[order._id]}
                        filters={orderEventFilters}
                        organizationTypeFilter={orgTypeFilters}
                        isMultipleOrders={false}
                    />
                </Grid>
            </Grid>
            <Fab
                sx={{
                    position: 'fixed',
                    bottom: (theme) => theme.spacing(2),
                    right: (theme) => theme.spacing(2),
                }}
                onClick={onScrollBottomClick}
            >
                <KeyboardArrowDownIcon />
            </Fab>
        </Fragment>
    );
};

export default OrderTimeline;
